/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useSystemMessages } from "../../hooks/useSystemMessages";
import { SubmitFeedbackDocument } from "../../services/__generated__/graphql";
import { theme } from "../../styles/theme/theme";

/**
 * Site-wide "Feedback" modal, opened from the left-rail icon. Submits a
 * free-text description to the `submitFeedback` mutation, which files a Linear
 * issue (with the submitter's email / customer / page URL auto-attached).
 *
 * Graceful degradation: if feedback delivery is unavailable (Linear creds not
 * yet seeded, API unreachable), the mutation returns success:false and we show
 * an inline "reach out to Atomic directly" fallback WITHOUT clearing the user's
 * text, so nothing they typed is lost.
 */
export function FeedbackModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { addMessage } = useSystemMessages();
  const [description, setDescription] = useState("");
  const [failed, setFailed] = useState(false);
  const [submitFeedback, { loading: submitting }] = useMutation(SubmitFeedbackDocument);

  const reset = () => {
    setDescription("");
    setFailed(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const text = description.trim();
    if (!text || submitting) return;
    setFailed(false);
    try {
      const result = await submitFeedback({
        variables: { description: text, pageUrl: window.location.href },
      });
      if (result.data?.submitFeedback?.success) {
        addMessage({
          type: "success",
          title: "Thanks — your feedback was sent",
          autoDismiss: true,
          dismissAfterMs: 4000,
        });
        close();
      } else {
        setFailed(true);
      }
    } catch {
      setFailed(true);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what's working, what's not, or what you'd like to see — we'll
            get it to the Atomic team. Your email and the current page are
            attached automatically.
          </DialogDescription>
        </DialogHeader>

        <label css={s.label}>
          <span>Your feedback</span>
          <textarea
            css={s.textarea}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder="e.g. The shipments chart shows no data after I change the date range."
            autoFocus
          />
        </label>

        {failed && (
          <div css={s.failure}>
            Feedback is temporarily unavailable. Please reach out to the Atomic
            team directly so we can help right away.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSubmit}
            disabled={description.trim().length === 0 || submitting}
          >
            {submitting ? "Sending…" : "Send feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const s = {
  label: css`
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
  `,
  textarea: css`
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid ${theme.colors.border};
    border-radius: 0.375rem;
    font: inherit;
    font-weight: 400;
    resize: vertical;
    min-height: 5rem;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${theme.colors.blue600};
    }
  `,
  failure: css`
    background: ${theme.colors.red50};
    color: ${theme.colors.red700};
    padding: 0.5rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.8125rem;
  `,
};
